import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'

const cleanExcess = (v) => (Number.isFinite(Number(v)) ? Math.max(0, Number(v)) : 0)

function transformEntry(row, levels, readings) {
  return {
    id: row.id,
    branchId: row.branch_id,
    product: row.product,
    date: row.date,
    session: row.session || 'complete',
    pricePerLiter: row.price_per_liter != null ? Number(row.price_per_liter) : null,
    tanks: levels.filter((l) => l.daily_entry_id === row.id).map((l) => ({
      tankId: l.tank_id,
      openingLevel: Number(l.opening_level),
      closingLevel: l.closing_level != null ? Number(l.closing_level) : null,
      supplyReceived: Number(l.supply_received),
    })),
    pumps: readings.filter((r) => r.daily_entry_id === row.id).map((r) => ({
      pumpId: r.pump_id,
      openingMeter: r.opening_meter != null ? Number(r.opening_meter) : null,
      closingMeter: r.closing_meter != null ? Number(r.closing_meter) : null,
      excessPer20L: r.excess_per_20l != null ? Number(r.excess_per_20l) : 1.0,
      openingImagePath: r.opening_image_path ?? null,
      closingImagePath: r.closing_image_path ?? null,
    })),
  }
}

async function insertChildren(entryId, input) {
  if (input.tanks?.length) {
    await supabase.from('tank_levels').insert(input.tanks.map((t) => ({
      daily_entry_id: entryId, tank_id: t.tankId,
      opening_level: t.openingLevel, closing_level: t.closingLevel ?? null, supply_received: t.supplyReceived ?? 0,
    })))
  }
  if (input.pumps?.length) {
    await supabase.from('pump_readings').insert(input.pumps.map((p) => ({
      daily_entry_id: entryId, pump_id: p.pumpId,
      opening_meter: p.openingMeter, closing_meter: p.closingMeter ?? null,
      excess_per_20l: cleanExcess(p.excessPer20L),
      opening_image_path: p.openingImagePath ?? null, closing_image_path: p.closingImagePath ?? null,
    })))
  }
}

async function replaceExpenses(branchId, date, expenses) {
  if (!expenses) return
  await supabase.from('expenses').delete().eq('branch_id', branchId).eq('date', date)
  if (expenses.length) {
    await supabase.from('expenses').insert(expenses.map((e) => ({ branch_id: branchId, date, description: e.description, amount: e.amount })))
  }
}

export function useDailyData() {
  const [entries, setEntries] = useState([])
  const [expenses, setExpenses] = useState([])

  useEffect(() => { fetchAll() }, [])

  async function fetchAll() {
    const [e, l, r, x] = await Promise.all([
      supabase.from('daily_entries').select('*').order('date', { ascending: false }),
      supabase.from('tank_levels').select('*'),
      supabase.from('pump_readings').select('*'),
      supabase.from('expenses').select('*'),
    ])
    setEntries((e.data || []).map((row) => transformEntry(row, l.data || [], r.data || [])))
    setExpenses((x.data || []).map((row) => ({ id: row.id, branchId: row.branch_id, date: row.date, description: row.description, amount: Number(row.amount) })))
  }

  function getLastComplete(branchId, product, beforeDate) {
    return entries.find((e) => e.branchId === branchId && e.product === product && e.date < beforeDate && e.session === 'complete') || null
  }

  // input: { branchId, product, date, session, pricePerLiter, tanks[], pumps[], expenses? }
  async function addEntry(input) {
    const { data: row, error } = await supabase.from('daily_entries').insert({
      branch_id: input.branchId, product: input.product, date: input.date, session: 'morning_open', price_per_liter: null,
    }).select().single()
    if (error) {
      if (error.code === '23505') return { success: false, message: `A ${input.product} entry for ${input.date} already exists.` }
      return { success: false, message: 'Failed to save: ' + error.message }
    }
    await insertChildren(row.id, input)
    await fetchAll()
    return { success: true, message: `Morning entry for ${input.product} saved.` }
  }

  // Bulk import: insert a COMPLETED day (session=complete, price set) plus its
  // tank levels, pump readings and expenses in one shot. Does not refetch — the
  // caller refetches once after the whole batch. Expenses are appended (a branch
  // day is split across product rows in the CSV, so replacing would clobber).
  async function importEntry(input) {
    const { data: row, error } = await supabase.from('daily_entries').insert({
      branch_id: input.branchId, product: input.product, date: input.date,
      session: 'complete', price_per_liter: input.pricePerLiter ?? null,
    }).select().single()
    if (error) {
      if (error.code === '23505') return { success: false, message: `A ${input.product} entry for ${input.date} already exists.` }
      return { success: false, message: error.message }
    }
    await insertChildren(row.id, input)
    if (input.expenses?.length) {
      await supabase.from('expenses').insert(input.expenses.map((e) => ({
        branch_id: input.branchId, date: input.date, description: e.description, amount: e.amount,
      })))
    }
    return { success: true }
  }

  async function updateEntry(input, entryId) {
    await supabase.from('daily_entries').update({ session: input.session || 'complete', price_per_liter: input.pricePerLiter ?? null }).eq('id', entryId)
    await supabase.from('tank_levels').delete().eq('daily_entry_id', entryId)
    await supabase.from('pump_readings').delete().eq('daily_entry_id', entryId)
    await insertChildren(entryId, input)
    await replaceExpenses(input.branchId, input.date, input.expenses)
    await fetchAll()
    return { success: true, message: `Data for ${input.date} saved.` }
  }

  async function deleteEntry(entryId) {
    await supabase.from('daily_entries').delete().eq('id', entryId)
    await fetchAll()
    return { success: true, message: 'Entry deleted.' }
  }

  return { entries, expenses, addEntry, importEntry, updateEntry, deleteEntry, getLastComplete, refetch: fetchAll }
}
